import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getRatesMatrix, getRatesGraphTimeLine } from 'selectors';
import {
  generateDatasets,
  DEFAULT_SOURCE_CURRENCY,
  getDate,
  DEFAULT_MOBILE_DATE_FORMAT,
  getEndDate,
  formatParsedDate,
} from 'utils';

import { DynamicRatesChart } from 'components/DynamicRatesChart';
import { useParams } from 'react-router-dom';

const terms = [
  { type: 'sell', value: 'exchangers' },
  { type: 'buy', value: 'exchangers' },
];

export const ExchangeChart = ({ chartTitle, type }) => {
  const { formatMessage, locale } = useIntl();
  const { currency, date: currentDate } = useParams();
  const [date, setDate] = useState({
    from: '',
    to: '',
    custom: {},
  });
  const [period, changePeriod] = useState('day');

  const [currentOption, setCurrentOption] = useState(
    currency?.toUpperCase() || DEFAULT_SOURCE_CURRENCY.toUpperCase()
  );

  const params = useMemo(
    () => ({
      sourceCurrency: currentOption,
      targetCurrency: 'UAH',
      period,
      date,
      locale,
      type,
      requestType: type,
    }),
    [
      date.from,
      date.to,
      locale,
      currentOption,
      date.custom?.from,
      date.custom?.to,
      period,
      currentDate,
    ]
  );

  const termsWithLocale = useMemo(() => {
    return terms.map(term => ({
      ...term,
      label: formatMessage({ id: `${term.value}.${[term.type]}` }),
    }));
  }, [terms]);

  const ratesMatrix = useSelector(state => getRatesMatrix(state, params));
  const ratesGraphTimeLine = useSelector(state =>
    getRatesGraphTimeLine(state, params)
  );

  useEffect(() => {
    if (date.custom?.from || date.custom?.to || !ratesGraphTimeLine?.from)
      return;

    if (currentDate) {
      const { dateFrom, dateTo } = getDate(currentDate, period);

      setDate({
        ...date,
        from: dateFrom,
        to: dateTo,
      });
    }

    if (!currentDate) {
      const startDate = formatParsedDate(
        ratesGraphTimeLine.from.date,
        DEFAULT_MOBILE_DATE_FORMAT
      );
      const endDate = getEndDate();

      setDate({ from: startDate, to: endDate });
    }
  }, [currentDate, period, ratesGraphTimeLine?.from]);

  const datasets = generateDatasets(termsWithLocale, ratesMatrix, type);

  return (
    <DynamicRatesChart
      params={params}
      changePeriod={changePeriod}
      terms={terms}
      currentOption={currentOption}
      setCurrentOption={setCurrentOption}
      ratesGraphTimeLine={ratesGraphTimeLine?.list}
      datasets={datasets}
      date={date}
      setDate={setDate}
      type="line"
      chartTitle={chartTitle}
      isOpenChart={false}
      page={type}
      hasDateRange={false}
    />
  );
};

ExchangeChart.propTypes = {
  chartTitle: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};
